import { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
import { fetchData } from '../../../apiService';
import LogoLoader from '../../common/LogoLoader';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { Refresh, GetApp, ArrowBack } from '@mui/icons-material';
import { IconButton, Tooltip, Alert, Snackbar } from '@mui/material';
import { exportStyledExcel } from '../../../utils/excelExport';

const PRIMARY     = '#1b5e20';
const PRIMARY_MID = '#2e7d32';
const PRIMARY_LT  = '#e8f5e9';

const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const fmtV = (n) => (parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmt2Date = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const iconBtn = {
    border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px',
    height: 29, width: 29, color: '#fff', flexShrink: 0,
    '&:hover': { background: 'rgba(255,255,255,0.1)' },
    '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' },
};

const GrowthBadge = ({ cur, prev }) => {
    if (!prev) return null;
    const pct = ((cur - prev) / prev) * 100;
    const up = pct >= 0;
    return (
        <span style={{
            marginLeft: 4, fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 8,
            background: up ? '#dcfce7' : '#fee2e2',
            color: up ? '#059669' : '#dc2626',
        }}>
            {up ? '▲' : '▼'} {Math.abs(pct).toFixed(1)}%
        </span>
    );
};

// Group flat rows: { itemId -> { itemName, months: { monthNum -> { cur, prev } } } }
const buildPivot = (rows) => {
    const map = {};
    const order = [];
    const monthSet = new Set();
    rows.forEach(r => {
        const key = r.ItemId;
        if (!map[key]) { map[key] = { ItemId: key, ItemName: r.ItemName, months: {} }; order.push(key); }
        map[key].months[r.Month] = { cur: parseFloat(r.CurrentYear) || 0, prev: parseFloat(r.PreviousYear) || 0 };
        monthSet.add(r.Month);
    });
    const months = [...monthSet].sort((a, b) => a - b);
    return { items: order.map(k => map[k]), months };
};

const YearOnYearSalesReport = () => {
    useSessionValidation();
    const { companyId, branchId, token, companyName, username } = useAuth();
    const { logAudit } = useAuditLog();
    const history = useHistory();

    const today = new Date();
    const [fromDate, setFromDate] = useState(fmt2Date(new Date(today.getFullYear(), 3, 1))); // Apr 1
    const [toDate, setToDate]     = useState(fmt2Date(today));
    const [rawData, setRawData]   = useState([]);
    const [loading, setLoading]   = useState(false);
    const [error, setError]       = useState('');
    const [fetched, setFetched]   = useState(false);
    const [search, setSearch]     = useState('');
    const [view, setView]         = useState('current'); // 'current' | 'previous' | 'both'

    useEffect(() => {
        if (companyId && branchId && token && fromDate && toDate) {
      if (new Date(fromDate) > new Date(toDate)) {
        setData([]);
        setFetched(false);
        setError('From Date cannot be greater than To Date');
        return;
      }
      fetchReport();
    }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyId, branchId, token]);

    const fetchReport = async () => {
        if (!fromDate || !toDate) { setError('Please select date range'); return; }
        if (new Date(fromDate) > new Date(toDate)) { setError('From Date cannot be greater than To Date'); return; }
        setLoading(true);
        try {
            const res = await fetchData(
                'SalesAnalysisReportApi',
                `/YearToYearComparison/${fromDate}/${toDate}/${companyId}/${branchId}`,
                token
            );
            const rows = res?.response_data || res || [];
            setRawData(Array.isArray(rows) ? rows : []);
            setFetched(true);
        } catch (err) {
            console.error(err);
            setError('Failed to fetch year on year comparison');
        } finally {
            setLoading(false);
        }
    };

    const { items, months } = useMemo(() => buildPivot(rawData), [rawData]);

    const filtered = useMemo(() => {
        if (!search.trim()) return items;
        const q = search.toLowerCase();
        return items.filter(r => (r.ItemName || '').toLowerCase().includes(q));
    }, [items, search]);

    // Grand totals per month
    const monthTotals = useMemo(() => {
        const t = {};
        months.forEach(m => {
            t[m] = { cur: 0, prev: 0 };
            filtered.forEach(r => {
                const d = r.months[m] || { cur: 0, prev: 0 };
                t[m].cur  += d.cur;
                t[m].prev += d.prev;
            });
        });
        return t;
    }, [filtered, months]);

    const grandTotals = useMemo(() => filtered.reduce((acc, r) => {
        Object.values(r.months).forEach(d => { acc.cur += d.cur; acc.prev += d.prev; });
        return acc;
    }, { cur: 0, prev: 0 }), [filtered]);

    const handleExport = async () => {
        logAudit(AUDIT_ACTION.EXPORT, 'YearOnYearSales', 0);
        if (!filtered.length) return;
        const subHeaders = months.flatMap(m => [`${MONTH_NAMES[(m - 1) % 12]} Cur`, `${MONTH_NAMES[(m - 1) % 12]} Prev`]);
        const headers = ['#', 'Item Name', ...subHeaders, 'Total Cur', 'Total Prev'];
        const rows = filtered.map((r, i) => {
            let tc = 0, tp = 0;
            const cells = months.flatMap(m => {
                const d = r.months[m] || { cur: 0, prev: 0 };
                tc += d.cur; tp += d.prev;
                return [d.cur, d.prev];
            });
            return [i + 1, r.ItemName, ...cells, tc, tp];
        });
        const totalRow = ['', 'Totals', ...months.flatMap(m => [monthTotals[m].cur, monthTotals[m].prev]), grandTotals.cur, grandTotals.prev];

        await exportStyledExcel({
            reportTitle: 'YEAR ON YEAR SALES COMPARISON',
            sheetName: 'YoY Sales',
            fileName: `YearOnYearSales_${fromDate}_${toDate}.xlsx`,
            companyName,
            username,
            fromDate,
            toDate,
            headers: headers,
            rows,
            totalsRow: totalRow,
        });
    };

    const thBase = { padding: '6px 8px', fontWeight: 700, fontSize: 10, whiteSpace: 'nowrap', border: '1px solid #388e3c', color: '#fff', background: PRIMARY_MID };
    const tdBase = { padding: '5px 8px', fontSize: 10, whiteSpace: 'nowrap', border: '1px solid #e2e8f0' };

    const viewOptions = [
        { key: 'current',  label: 'Current Year' },
        { key: 'previous', label: 'Previous Year' },
        { key: 'both',     label: 'Both' },
    ];

    return (
        <div style={{ minHeight: '100%', background: '#f0f2f5', padding: 12, fontFamily: "'Inter', sans-serif", boxSizing: 'border-box' }}>
            <LogoLoader open={loading} message="Loading year on year comparison..." />
            <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert severity="error" onClose={() => setError('')} sx={{ width: '100%' }}>{error}</Alert>
            </Snackbar>

            <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0', overflow: 'hidden' }}>

                {/* Toolbar */}
                <div className="report-toolbar" style={{ display: 'flex', alignItems: 'center', padding: '6px 12px', background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_MID} 100%)`, gap: 6, flexWrap: 'nowrap', overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'none' }}>
                    <Tooltip title="Back" arrow>
                        <IconButton onClick={() => history.push('/ReportsHub')} size="small" sx={iconBtn}>
                            <ArrowBack sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', flexShrink: 0 }}>
                        Year On Year Sales
                        <span style={{ fontSize: 11, fontWeight: 400, opacity: 0.8, display: 'block', lineHeight: 1.2 }}>Sales Analysis Report</span>
                    </span>
                    <div style={{ flex: 1, minWidth: 4 }} />
                    {/* View toggle */}
                    <div style={{ display: 'flex', gap: 3, flexShrink: 0 }}>
                        {viewOptions.map(o => (
                            <button key={o.key} onClick={() => setView(o.key)}
                                style={{ height: 26, padding: '0 10px', fontSize: 10, fontWeight: 700, borderRadius: 6, cursor: 'pointer',
                                    border: `1px solid ${view === o.key ? '#fff' : 'rgba(255,255,255,0.3)'}`,
                                    background: view === o.key ? '#fff' : 'rgba(255,255,255,0.1)',
                                    color: view === o.key ? PRIMARY : '#fff', transition: 'all 0.15s' }}>
                                {o.label}
                            </button>
                        ))}
                    </div>
                    <input placeholder="Search item..."
                        value={search} onChange={e => setSearch(e.target.value)}
                        style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 10px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0, minWidth: 150, outline: 'none' }} />
                    <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
                        style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
                    <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
                        style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
                    <Tooltip title="Refresh" arrow>
                        <IconButton onClick={fetchReport} disabled={loading} size="small" sx={iconBtn}>
                            <Refresh sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Export Excel" arrow>
                        <IconButton onClick={handleExport} disabled={!filtered.length} size="small" sx={iconBtn}>
                            <GetApp sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                </div>

                {/* Summary bar */}
                {fetched && filtered.length > 0 && (
                    <div style={{ display: 'flex', gap: 8, padding: '6px 14px', borderBottom: '1px solid #f1f5f9', flexWrap: 'wrap' }}>
                        {[
                            { label: 'Items',         value: filtered.length,               color: PRIMARY },
                            { label: 'Current Year',  value: `₹${fmtV(grandTotals.cur)}`,  color: '#059669' },
                            { label: 'Previous Year', value: `₹${fmtV(grandTotals.prev)}`, color: '#0277bd' },
                            { label: 'Growth',
                              value: grandTotals.prev
                                ? `${((grandTotals.cur - grandTotals.prev) / grandTotals.prev * 100).toFixed(1)}%`
                                : '-',
                              color: grandTotals.cur >= grandTotals.prev ? '#059669' : '#dc2626' },
                        ].map(s => (
                            <div key={s.label} style={{ background: PRIMARY_LT, borderRadius: 8, padding: '4px 12px', display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 100 }}>
                                <span style={{ fontSize: 10, color: '#64748b', fontWeight: 600 }}>{s.label}</span>
                                <span style={{ fontSize: 12, color: s.color, fontWeight: 700 }}>{s.value}</span>
                            </div>
                        ))}
                    </div>
                )}

                {/* Meta */}
                {fetched && (
                    <div style={{ padding: '4px 14px', fontSize: 11, color: '#64748b', borderBottom: '1px solid #f1f5f9' }}>
                        {fromDate} to {toDate} &nbsp;|&nbsp; {filtered.length} items
                        {search && ` (filtered from ${items.length})`}
                    </div>
                )}

                {/* Table */}
                <div className="report-table-scroll" style={{ overflow: 'auto', overflowY: 'scroll', maxHeight: 'calc(100vh - 190px)' }}>
                    <table style={{ borderCollapse: 'collapse', fontSize: 10, width: '100%' }}>
                        <thead style={{ position: 'sticky', top: 0, zIndex: 3 }}>
                            {/* Month header row */}
                            <tr>
                                <th style={{ ...thBase, textAlign: 'center', width: 32 }} rowSpan={2}>#</th>
                                <th style={{ ...thBase, textAlign: 'left', minWidth: 200 }} rowSpan={2}>Item Name</th>
                                {months.map(m => (
                                    <th key={m} style={{ ...thBase, textAlign: 'center', minWidth: view === 'both' ? 160 : 90 }}
                                        colSpan={view === 'both' ? 2 : 1}>
                                        {MONTH_NAMES[(m - 1) % 12]}
                                    </th>
                                ))}
                                <th style={{ ...thBase, textAlign: 'center', minWidth: view === 'both' ? 160 : 90, background: '#1a3a1a' }}
                                    colSpan={view === 'both' ? 2 : 1}>Total</th>
                            </tr>
                            {/* Sub-header for both view */}
                            {view === 'both' && (
                                <tr>
                                    {months.map(m => (
                                        <>
                                            <th key={`${m}-c`} style={{ ...thBase, textAlign: 'right', fontSize: 9, background: '#388e3c' }}>Cur</th>
                                            <th key={`${m}-p`} style={{ ...thBase, textAlign: 'right', fontSize: 9, background: '#1565c0' }}>Prev</th>
                                        </>
                                    ))}
                                    <th style={{ ...thBase, textAlign: 'right', fontSize: 9, background: '#1a3a1a' }}>Cur</th>
                                    <th style={{ ...thBase, textAlign: 'right', fontSize: 9, background: '#0d47a1' }}>Prev</th>
                                </tr>
                            )}
                        </thead>
                        <tbody>
                            {filtered.length === 0 ? (
                                <tr>
                                    <td colSpan={2 + months.length * (view === 'both' ? 2 : 1) + (view === 'both' ? 2 : 1)}
                                        style={{ textAlign: 'center', padding: 32, color: '#94a3b8', fontSize: 13 }}>
                                        {fetched ? 'No records found' : 'Select date range and click Refresh'}
                                    </td>
                                </tr>
                            ) : filtered.map((r, i) => {
                                let totalCur = 0, totalPrev = 0;
                                return (
                                    <tr key={r.ItemId} style={{ background: i % 2 === 0 ? '#fff' : PRIMARY_LT }}>
                                        <td style={{ ...tdBase, textAlign: 'center', color: '#94a3b8' }}>{i + 1}</td>
                                        <td style={{ ...tdBase, fontWeight: 600, color: PRIMARY }}>{r.ItemName || '-'}</td>
                                        {months.map(m => {
                                            const d = r.months[m] || { cur: 0, prev: 0 };
                                            totalCur  += d.cur;
                                            totalPrev += d.prev;
                                            if (view === 'current') return (
                                                <td key={m} style={{ ...tdBase, textAlign: 'right', color: d.cur ? '#059669' : '#cbd5e1' }}>
                                                    {d.cur ? fmtV(d.cur) : '-'}
                                                </td>
                                            );
                                            if (view === 'previous') return (
                                                <td key={m} style={{ ...tdBase, textAlign: 'right', color: d.prev ? '#0277bd' : '#cbd5e1' }}>
                                                    {d.prev ? fmtV(d.prev) : '-'}
                                                </td>
                                            );
                                            return (
                                                <>
                                                    <td key={`${m}-c`} style={{ ...tdBase, textAlign: 'right', color: d.cur ? '#059669' : '#cbd5e1' }}>
                                                        {d.cur ? fmtV(d.cur) : '-'}
                                                    </td>
                                                    <td key={`${m}-p`} style={{ ...tdBase, textAlign: 'right', color: d.prev ? '#0277bd' : '#cbd5e1' }}>
                                                        {d.prev ? fmtV(d.prev) : '-'}
                                                        {d.cur > 0 && d.prev > 0 && <GrowthBadge cur={d.cur} prev={d.prev} />}
                                                    </td>
                                                </>
                                            );
                                        })}
                                        {/* Row totals */}
                                        {view === 'current' && <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: '#059669' }}>{fmtV(totalCur)}</td>}
                                        {view === 'previous' && <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: '#0277bd' }}>{fmtV(totalPrev)}</td>}
                                        {view === 'both' && <>
                                            <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: '#059669' }}>{fmtV(totalCur)}</td>
                                            <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: '#0277bd' }}>
                                                {fmtV(totalPrev)}
                                                {totalCur > 0 && totalPrev > 0 && <GrowthBadge cur={totalCur} prev={totalPrev} />}
                                            </td>
                                        </>}
                                    </tr>
                                );
                            })}
                        </tbody>
                        {filtered.length > 0 && (
                            <tfoot>
                                <tr style={{ background: PRIMARY_LT, fontWeight: 700, position: 'sticky', bottom: 0 }}>
                                    <td colSpan={2} style={{ ...tdBase, fontWeight: 700, color: PRIMARY, textAlign: 'right' }}>Totals</td>
                                    {months.map(m => {
                                        const t = monthTotals[m] || { cur: 0, prev: 0 };
                                        if (view === 'current') return <td key={m} style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: '#059669' }}>{fmtV(t.cur)}</td>;
                                        if (view === 'previous') return <td key={m} style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: '#0277bd' }}>{fmtV(t.prev)}</td>;
                                        return (
                                            <>
                                                <td key={`${m}-c`} style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: '#059669' }}>{fmtV(t.cur)}</td>
                                                <td key={`${m}-p`} style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: '#0277bd' }}>{fmtV(t.prev)}</td>
                                            </>
                                        );
                                    })}
                                    {view === 'current' && <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: '#059669' }}>{fmtV(grandTotals.cur)}</td>}
                                    {view === 'previous' && <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: '#0277bd' }}>{fmtV(grandTotals.prev)}</td>}
                                    {view === 'both' && <>
                                        <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: '#059669' }}>{fmtV(grandTotals.cur)}</td>
                                        <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: '#0277bd' }}>{fmtV(grandTotals.prev)}</td>
                                    </>}
                                </tr>
                            </tfoot>
                        )}
                    </table>
                </div>
            </div>
        </div>
    );
};

export default YearOnYearSalesReport;
